import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { getFaqConfig, saveFaqConfig } from '../utils/faqFirestore';
import RichTextEditor from '../components/RichTextEditor';

const cardStyle = {
  marginBottom: '16px',
  padding: '20px 24px',
  backgroundColor: 'rgba(255, 255, 255, 0.06)',
  borderRadius: '8px',
  border: '1px solid rgba(255, 255, 255, 0.12)'
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  marginTop: '6px',
  marginBottom: '12px',
  fontFamily: 'var(--font-body)',
  fontSize: '1rem',
  color: 'var(--text-primary)',
  backgroundColor: 'rgba(0,0,0,0.2)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '6px',
  boxSizing: 'border-box'
};

const labelStyle = {
  display: 'block',
  fontFamily: 'var(--font-body)',
  fontSize: '0.9rem',
  color: 'var(--text-secondary)',
  marginBottom: '4px'
};

function FAQEdit() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const [items, setItems] = useState([]);
  const [transport, setTransport] = useState({ title: '', description: '', companies: [{ name: '', phone: '' }] });

  useEffect(() => {
    const init = async () => {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user?.phoneNumber) {
        setLoading(false);
        return;
      }
      try {
        const invitesRef = collection(db, 'invites');
        const userSnap = await getDocs(query(invitesRef, where('phoneNumber', '==', user.phoneNumber)));
        if (userSnap.empty) {
          setLoading(false);
          return;
        }
        const role = userSnap.docs[0].data().role?.toLowerCase();
        if (role !== 'groom' && role !== 'bride') {
          setLoading(false);
          return;
        }
        setAllowed(true);
        const config = await getFaqConfig();
        setItems(config.items.map(it => ({
          question: it.question || '',
          answer: it.answer || '',
          answerEvening: it.answerEvening ?? '',
          showTo: it.showTo || 'both'
        })));
        setTransport(config.transport || {
          title: 'Transport',
          description: '',
          companies: [{ name: '', phone: '' }]
        });
      } catch (err) {
        console.error('Error loading FAQ edit:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const handleItemChange = (index, field, value) => {
    setItems(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addItem = () => {
    setItems(prev => [...prev, { question: '', answer: '', answerEvening: '', showTo: 'both' }]);
  };

  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleTransportChange = (field, value) => {
    setTransport(prev => ({ ...prev, [field]: value }));
  };

  const handleCompanyChange = (index, field, value) => {
    setTransport(prev => {
      const companies = [...(prev.companies || [])];
      companies[index] = { ...(companies[index] || { name: '', phone: '' }), [field]: value };
      return { ...prev, companies };
    });
  };

  const addCompany = () => {
    setTransport(prev => ({
      ...prev,
      companies: [...(prev.companies || []), { name: '', phone: '' }]
    }));
  };

  const removeCompany = (index) => {
    setTransport(prev => ({
      ...prev,
      companies: prev.companies.filter((_, i) => i !== index)
    }));
  };

  const isBlankHtml = (html) => {
    if (!html || !html.trim()) return true;
    const stripped = html.replace(/<[^>]+>/g, '').trim();
    return stripped === '';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        items: items.map(({ question, answer, answerEvening, showTo }) => ({
          question: question.trim(),
          answer: (answer || '').trim(),
          showTo: showTo || 'both',
          ...(!isBlankHtml(answerEvening) ? { answerEvening: answerEvening.trim() } : {})
        })).filter(it => it.question),
        transport: {
          title: transport.title || 'Transport',
          description: transport.description || '',
          companies: (transport.companies || []).filter(c => c.name || c.phone).length
            ? transport.companies.filter(c => c.name || c.phone)
            : [{ name: '', phone: '' }]
        }
      };
      await saveFaqConfig(payload);
      navigate('/faq');
    } catch (err) {
      console.error('Save failed:', err);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container" style={{ textAlign: 'center', color: 'var(--text-primary)' }}>
        Loading…
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="page-container" style={{ textAlign: 'center', color: 'var(--text-primary)' }}>
        <p>Only the bride and groom can edit FAQs.</p>
        <Link to="/faq" style={{ color: 'var(--accent-gold)', textDecoration: 'underline' }}>Back to FAQ</Link>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
          <h2 className="page-title" style={{ margin: 0 }}>Edit FAQs</h2>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <Link to="/faq" style={{ color: 'var(--accent-gold)', textDecoration: 'underline' }}>View FAQ</Link>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="card"
              style={{
                padding: '10px 20px',
                cursor: saving ? 'not-allowed' : 'pointer',
                border: '1px solid var(--accent-gold)',
                color: 'var(--accent-gold)',
                background: 'transparent',
                fontFamily: 'var(--font-body)',
                fontSize: '1rem'
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        <p className="page-subtitle" style={{ marginBottom: '24px' }}>
          Day guests see &quot;Answer (day)&quot;. Evening guests see &quot;Answer (evening)&quot; when you fill it in; otherwise they see the same as day.
        </p>

        {items.map((item, index) => (
          <div key={index} style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ ...labelStyle, marginBottom: 0 }}>FAQ #{index + 1}</span>
              <button
                type="button"
                onClick={() => removeItem(index)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '0.9rem'
                }}
              >
                Remove
              </button>
            </div>
            <label style={labelStyle}>Show to</label>
            <select
              value={item.showTo || 'both'}
              onChange={e => handleItemChange(index, 'showTo', e.target.value)}
              style={{ ...inputStyle, marginBottom: '12px', cursor: 'pointer' }}
            >
              <option value="both">Both (day &amp; evening guests)</option>
              <option value="day">Day guests only</option>
              <option value="evening">Evening guests only</option>
            </select>
            <label style={labelStyle}>Question</label>
            <input
              type="text"
              value={item.question}
              onChange={e => handleItemChange(index, 'question', e.target.value)}
              placeholder="e.g. What time should I arrive?"
              style={inputStyle}
            />
            <label style={labelStyle}>Answer (day guests)</label>
            <RichTextEditor
              value={item.answer}
              onChange={(html) => handleItemChange(index, 'answer', html)}
              placeholder="Type your answer — use the toolbar for bold, lists, links…"
              minHeight="120px"
            />
            <label style={{ ...labelStyle, marginTop: '16px' }}>Answer (evening guests) — leave blank to use same as day</label>
            <RichTextEditor
              value={item.answerEvening}
              onChange={(html) => handleItemChange(index, 'answerEvening', html)}
              placeholder="Optional: different text for evening-only guests"
              minHeight="80px"
            />
          </div>
        ))}

        <button
          type="button"
          onClick={addItem}
          className="card"
          style={{
            marginBottom: '32px',
            padding: '16px 24px',
            width: '100%',
            cursor: 'pointer',
            border: '1px dashed rgba(255,255,255,0.3)',
            color: 'var(--text-secondary)',
            background: 'transparent',
            fontFamily: 'var(--font-body)',
            fontSize: '1rem'
          }}
        >
          + Add FAQ
        </button>

        <div style={cardStyle}>
          <h3 style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-primary)', marginTop: 0, marginBottom: '16px' }}>Transport section</h3>
          <label style={labelStyle}>Title</label>
          <input
            type="text"
            value={transport.title}
            onChange={e => handleTransportChange('title', e.target.value)}
            style={inputStyle}
          />
          <label style={labelStyle}>Description</label>
          <textarea
            value={transport.description}
            onChange={e => handleTransportChange('description', e.target.value)}
            rows={2}
            style={inputStyle}
          />
          <label style={labelStyle}>Companies (name &amp; phone)</label>
          {(transport.companies || []).map((company, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={company.name}
                onChange={e => handleCompanyChange(idx, 'name', e.target.value)}
                placeholder="Name"
                style={{ ...inputStyle, flex: '1', minWidth: '120px', margin: 0 }}
              />
              <input
                type="text"
                value={company.phone}
                onChange={e => handleCompanyChange(idx, 'phone', e.target.value)}
                placeholder="Phone"
                style={{ ...inputStyle, flex: '1', minWidth: '120px', margin: 0 }}
              />
              <button
                type="button"
                onClick={() => removeCompany(idx)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addCompany}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--accent-gold)',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '0.9rem',
              padding: 0
            }}
          >
            + Add company
          </button>
        </div>

        <div style={{ marginTop: '24px', marginBottom: '40px' }}>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="card"
            style={{
              padding: '12px 24px',
              cursor: saving ? 'not-allowed' : 'pointer',
              border: '1px solid var(--accent-gold)',
              color: 'var(--accent-gold)',
              background: 'transparent',
              fontFamily: 'var(--font-body)',
              fontSize: '1rem'
            }}
          >
            {saving ? 'Saving…' : 'Save all changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FAQEdit;
